import {
  OrgInviteDeleteRequest,
  OrgMemberDeleteRequest,
  OrgMemberRoleUpdateRequest,
  OrgRoles,
  OrganizationService,
} from '@/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Workspaces } from './Workspaces';
import CreateNewOrgInvite from './create-new-org-invite';
import OrgRoleSelect from './org-role-select';

interface Props {
  organizationId: string;
}

export function ManageOrganization({ organizationId }: Props) {
  // query client
  const queryClient = useQueryClient();

  // current logged in user
  const user = useStore($user);

  // query to get the members of the organization
  const orgMembers = useQuery({
    queryKey: ['getOrgMembers', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await OrganizationService.getMembers(organizationId);

      return res;
    },
  });

  // query to get the invitations of the organization
  const orgInvitations = useQuery({
    queryKey: ['getOrgInvites', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await OrganizationService.getInvites(organizationId);

      return res;
    },
  });

  // mutation to remove a member from the organization
  const removeOrgMember = useMutation({
    mutationFn: async (payload: OrgMemberDeleteRequest) => {
      const res = await OrganizationService.deleteMember(
        organizationId,
        payload,
      );
      return res;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['getOrgMembers', organizationId],
      });
      toast('Member removed successfully', {
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);
      toast('Failed to remove member', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Mutation to change the role of a member in the organization
  const changeOrgRole = useMutation({
    mutationFn: async (payload: OrgMemberRoleUpdateRequest) => {
      const res = await OrganizationService.updateMemberRole(
        organizationId,
        payload,
      );
      return res;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['getOrgMembers', organizationId],
      });
      toast('Role changed successfully', {
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);
      toast('Failed to change role', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Mutation to delete an organization invite
  const deleteOrgInvite = useMutation({
    mutationFn: async (payload: OrgInviteDeleteRequest) => {
      const res = await OrganizationService.deleteInvite(
        organizationId,
        payload,
      );
      return res;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['getOrgInvites', organizationId],
      });
      toast('Invite deleted successfully', {
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);
      toast('Failed to delete invite', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // handle org role change
  async function handleOrgRoleChange(role: OrgRoles, profileId: string) {
    changeOrgRole.mutateAsync({
      profileId,
      role,
    });
  }

  // handle remove member
  async function handleRemoveMember(profileId: string) {
    removeOrgMember.mutateAsync({
      profileId,
    });
  }

  // handle delete invite
  async function handleDeleteInvite(inviteId: string) {
    // call the mutation to delete the invite
    deleteOrgInvite.mutateAsync({
      id: inviteId,
    });
  }

  return (
    <div className="flex flex-col gap-4 sm:max-w-2xl">
      <Tabs defaultValue="members" className="">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
          {/* <TabsTrigger value="workspaces">Workspaces</TabsTrigger> */}
        </TabsList>

        <TabsContent value="members">
          {/* Org member list */}
          <Card>
            <CardHeader>
              <CardTitle>Current Members</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6">
              {orgMembers.isLoading && <p>Loading...</p>}
              {orgMembers.isError && <p>Error: {orgMembers.error.message}</p>}

              {orgMembers.data &&
                orgMembers.data.map(orgMember => {
                  if (!orgMember.profile || !orgMember.role) return null;

                  return (
                    <div
                      key={orgMember.profile.id}
                      className="flex items-center justify-between space-x-4"
                    >
                      <div className="flex items-center space-x-4">
                        <Avatar className="">
                          <AvatarImage
                            src={orgMember.profile.photoUrl || ''}
                            alt="Profile menu"
                          />
                          <AvatarFallback>
                            {orgMember.profile.name
                              .slice(0, 2)
                              .toLocaleUpperCase() || 'A'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium leading-none">
                            {orgMember.profile.name || 'Name not found'}{' '}
                            {user?.uid === orgMember.profile.id && (
                              <Badge variant="outline">You</Badge>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {orgMember.profile.email || 'Email not found'}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        {/* TODO: Add API to remove/edit the collaborator */}

                        {/* Role dropdown */}
                        <OrgRoleSelect
                          value={orgMember.role}
                          disabled={user?.uid === orgMember.profile.id}
                          onChange={value =>
                            handleOrgRoleChange(
                              value,
                              orgMember.profile?.id || '',
                            )
                          }
                        />

                        {/* Remove Member button */}
                        <Button
                          variant="destructive"
                          onClick={() => {
                            handleRemoveMember(orgMember.profile?.id || '');
                          }}
                          disabled={
                            orgMember.role === 'Owner' ||
                            user?.uid === orgMember.profile.id
                          }
                        >
                          {removeOrgMember.isPending &&
                            removeOrgMember.variables.profileId ==
                              orgMember.profile.id && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations">
          {/* Create new org invite */}
          <CreateNewOrgInvite organizationId={organizationId} />

          {/* Org invitations list */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Invitations</CardTitle>
              <CardDescription>
                Manage your organization invitations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <ScrollArea className="max-h-[200px]">
                <div className="flex h-[200px] flex-col gap-6 pr-4">
                  {orgInvitations.isLoading && <p>Loading...</p>}
                  {orgInvitations.isError && (
                    <p>Error: {orgInvitations.error.message}</p>
                  )}

                  {/* No invitations */}
                  {orgInvitations.data?.length === 0 && (
                    <p className="text-center text-sm">No invitations found.</p>
                  )}

                  {orgInvitations.data &&
                    orgInvitations.data.map(orgInvite => {
                      return (
                        <div
                          key={orgInvite.id}
                          className="flex items-center justify-between space-x-4"
                        >
                          <div className="flex items-center space-x-4">
                            <Avatar className="">
                              <AvatarImage src={''} alt="Profile menu" />
                              <AvatarFallback>
                                {orgInvite.name
                                  .slice(0, 2)
                                  .toLocaleUpperCase() || 'A'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium leading-none">
                                {orgInvite.email || 'Email not found'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {orgInvite.role}
                              </p>
                            </div>
                          </div>

                          <div className="flex gap-4">
                            {/* Remove invite button */}
                            <Button
                              variant="destructive"
                              onClick={() => handleDeleteInvite(orgInvite.id)}
                            >
                              {deleteOrgInvite.isPending &&
                                deleteOrgInvite.variables.id ==
                                  orgInvite.id && (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                              Cancel Invite
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* <TabsContent value="workspaces">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Workspaces</CardTitle>
              <CardDescription>
                Manage your organization workspaces
              </CardDescription>
            </CardHeader>

            <CardContent className="">
              <Workspaces organizationId={organizationId} />
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>
    </div>
  );
}
